import React from "react";
import { useRouter } from "next/router";

import ModelViewer from "../../Common/ModelViewer";

/* ============================================================
   Planetary Land Registries — vertical marketplace listing cards.
   Each card: an auto-rotating 3D model hero (local NASA-derived GLB,
   transparent so the hero gradient shows through), a structured stat
   table, an availability progress bar, and a single primary CTA.
   ============================================================ */

type RegistryKind = "mars" | "moon";

interface RegistryCardProps {
  kind: RegistryKind;
  /** Planet name only — the muted "Land Registry" suffix is added in markup. */
  name: string;
  url: string;
  regions: number;
  price: string;
  lat: string;
  /** Real registry route, e.g. "/real-estate/mars". */
  href: string;
  /** Local GLB model rendered in the card hero, e.g. "/glb/marscompress.glb". */
  model: string;
  onClaim?: () => void;
}

function RegistryCard({ kind, name, url, regions, price, lat, href, model, onClaim }: RegistryCardProps) {
  const router = useRouter();

  const available = Math.round(regions * 0.62);
  const availablePct = Math.round((available / regions) * 100);

  const handleClaim = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    onClaim?.();
    router.push(href);
  };

  return (
    <article className="reg-card panel panel--hover ticks" data-reveal>
      <div className="reg-visual" data-kind={kind}>
        <span className="reg-badge">
          <span className="dot dot-live" />
          POWERED BY NASA VIZ
        </span>
        <ModelViewer
          src={model}
          alt={`${name} Land Registry — interactive 3D model`}
          style={{ position: "absolute", inset: 0 }}
        />
      </div>

      <div className="reg-body">
        <header className="reg-head">
          <h3 className="reg-name">
            {name} <span>Land Registry</span>
          </h3>
          <span className="reg-url">{url}</span>
        </header>

        <dl className="reg-stats">
          <div>
            <dt>REGIONS</dt>
            <dd className="tnum">{regions.toLocaleString()}</dd>
          </div>
          <div>
            <dt>FLOOR</dt>
            <dd className="tnum">{price} ETH</dd>
          </div>
          <div>
            <dt>SUB-SOLAR</dt>
            <dd className="tnum">{lat}</dd>
          </div>
          <div>
            <dt>STANDARD</dt>
            <dd>ERC-721</dd>
          </div>
        </dl>

        <div className="reg-avail">
          <div className="reg-avail-row">
            <span className="reg-avail-label">REGIONS AVAILABLE</span>
            <span className="reg-avail-val tnum">
              <b>{available.toLocaleString()}</b> / {regions.toLocaleString()}
            </span>
          </div>
          <div
            className="reg-bar"
            role="progressbar"
            aria-valuenow={availablePct}
            aria-valuemin={0}
            aria-valuemax={100}
            aria-label={`${availablePct}% of regions available`}
          >
            <i style={{ width: `${availablePct}%` }} />
          </div>
        </div>

        <a className="btn btn-primary reg-btn" href={href} onClick={handleClaim}>
          CLAIM A REGION <span className="ar">→</span>
        </a>
      </div>
    </article>
  );
}

interface RegistriesProps {
  onClaim?: () => void;
}

export default function Registries({ onClaim }: RegistriesProps) {
  return (
    <section id="registries" className="sec-pad" data-screen-label="Registries">
      <div className="wrap">
        <div className="sec-tag">
          <span className="idx">[ 02 ]</span> PLANETARY LAND REGISTRIES <span className="rule"></span>{" "}
          <span>SURFACE · TOKENIZED</span>
        </div>
        <h2 className="h-md reg-title" data-reveal>
          Claim verified surface regions of Mars &amp; the Moon, gridded from NASA terrain data.
        </h2>
        <div className="reg-cards">
          <RegistryCard
            kind="mars"
            name="Mars"
            url="marsmodal.niftysky.org/mars"
            model="/glb/marscompress.glb"
            regions={1827}
            price="1.00"
            lat="18.6°N 226°E"
            href="/real-estate/mars"
            onClaim={onClaim}
          />
          <RegistryCard
            kind="moon"
            name="Moon"
            url="modalmoon.niftysky.org/earthsmoon"
            model="/glb/earthmooncompress.glb"
            regions={9014}
            price="0.50"
            lat="43.3°S 11°W"
            href="/real-estate/moon"
            onClaim={onClaim}
          />
        </div>
      </div>
    </section>
  );
}
